// The SOA signing-invitation email's wording plumbing (Ryan Delviken,
// 2026-08-30: "on the email that gets sent, can we update the copy").
//
// The WORDING lives in a communication_templates row (slug below),
// seeded with Ryan's copy verbatim — the automation-first rule: message
// wording never lives in code, so Ryan rewords this from the templates
// library without a deploy. The constants here are the compiled
// FALLBACK, used only if the seeded row is missing.
//
// The invitation is the OS's own email from the advisor's mailbox (the
// provider's emails are disabled account-wide — see boldsign-client's
// DisableEmails note), so "the link below" in Ryan's copy refers to the
// OS's Review & sign button. That button is markup the route builds
// (signingButtonHtml in routes/esign.ts); the template refers to it by
// the {{review_and_sign_button}} token, the same pattern as the plan
// recommendation's {{comparison_table}} — a structural block nobody
// would reword, positioned by the person editing the words around it.
//
// Pure module: no DB, no env — node-pool testable.

export const SOA_INVITATION_TEMPLATE_SLUG = "planning_soa_signing_invitation";

export const SOA_INVITATION_BUTTON_TOKEN = "{{review_and_sign_button}}";

export const SOA_INVITATION_FALLBACK_SUBJECT =
  "Signature requested: Scope of Appointment";

// Ryan's copy, verbatim (2026-08-30) — his "[Review & Sign]" line is the
// button token, since that button is the provider-link he was pointing at.
export const SOA_INVITATION_FALLBACK_BODY = `Before we discuss Medicare plan options, Medicare requires us to have a signed Scope of Appointment (SOA) on file.

We've made this easy to complete electronically. Please use the link below to review and sign the form before our conversation:

{{review_and_sign_button}}

The SOA simply gives us permission to discuss the Medicare products indicated on the form with you. Signing it does not obligate you to enroll in a plan, purchase anything, or do business with us.

It's simply a Medicare compliance requirement that allows us to have the conversation and help you understand your options.

If you have any questions or have trouble completing the form, please let me know.`;

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Renders the stored plain-text body into the email's core HTML:
 * paragraphs split on blank lines, everything escaped, and the button
 * token swapped for the real (trusted, OS-built) button markup. A
 * paragraph that is ONLY the token becomes the button block itself
 * rather than a table wrapped in a <p>. A template with no token still
 * gets the button appended at the end — the email must never go out
 * without its link, whatever an edit did to the wording.
 */
export function buildSoaInvitationBodyHtml(
  bodyTemplate: string,
  buttonHtml: string,
): string {
  const paragraphs = bodyTemplate
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter((p) => p !== "");

  let sawToken = false;
  const rendered = paragraphs.map((p) => {
    if (p === SOA_INVITATION_BUTTON_TOKEN) {
      sawToken = true;
      return buttonHtml;
    }
    const escaped = escapeHtml(p);
    if (escaped.includes(SOA_INVITATION_BUTTON_TOKEN)) {
      sawToken = true;
      // Inline usage: close the paragraph around the block-level button.
      return `<p>${escaped.split(SOA_INVITATION_BUTTON_TOKEN).join(`</p>${buttonHtml}<p>`)}</p>`;
    }
    return `<p>${escaped.replace(/\n/g, "<br/>")}</p>`;
  });

  if (!sawToken) rendered.push(buttonHtml);
  return rendered.join("");
}
